// External modules
import { Translation } from '../models/translation';

// Internal modules
import { environment } from '../../../environments/environment';

// Interfaces
import { Image }       from '../interfaces/image.interface';

// Enums
import { StorageKey }  from '../enums/storage-key.enum';

export class IndexedDbHelper
{
  public static getTranslations(code: string) : Promise<Translation[]|null>
  {
    return this.getItem(StorageKey.TRANSLATIONS_STORE, code);
  }

  public static setTranslations(code: string, translations: Translation[]) : Promise<void>
  {
    return this.setItem(StorageKey.TRANSLATIONS_STORE, code, translations);
  }

  public static clearTranslations() : Promise<void>
  {
    return this.clear(StorageKey.TRANSLATIONS_STORE);
  }

  public static getImage(name: string) : Promise<Image|null>
  {
    return this.getItem(StorageKey.IMAGES_STORE, name);
  }

  public static getImageKeys() : Promise<string[]>
  {
    return this.getItemKeys(StorageKey.IMAGES_STORE) as Promise<string[]>;
  }

  public static setImage(name: string, image: any) : Promise<void>
  {
    return this.setItem(StorageKey.IMAGES_STORE, name, image);
  }

  public static clearImages() : Promise<void>
  {
    return this.clear(StorageKey.IMAGES_STORE);
  }

  private static getDatabase() : Promise<IDBDatabase>
  {
    return new Promise((resolve, reject) =>
    {
      const openRequest = indexedDB.open("MapApp", environment.VERSION);

      openRequest.onupgradeneeded = function()
      {
        const database = openRequest.result;
        if (!database.objectStoreNames.contains(StorageKey.TRANSLATIONS_STORE))
        {
          console.log("Creating translations store");

          database.createObjectStore(StorageKey.TRANSLATIONS_STORE);
        }

        if (!database.objectStoreNames.contains(StorageKey.IMAGES_STORE))
        {
          console.log("Creating images store");
          database.createObjectStore(StorageKey.IMAGES_STORE);
        }
      };

      openRequest.onerror = function()
      {
        reject(openRequest.error);
      };

      openRequest.onsuccess = function()
      {
        resolve(openRequest.result);
      };
    });
  }

  private static async setItem(store: string, key: string, value: any) : Promise<void>
  {
    const database = await this.getDatabase();

    return new Promise<void>((resolve, reject) =>
    {
      const transaction = database.transaction(store, "readwrite");
      const objectStore = transaction.objectStore(store);
      const request     = objectStore.put(value, key);

      request.onsuccess = function()
      {
        resolve();
      };

      request.onerror = function()
      {
        reject(request.error);
      };
    });
  }

  private static async getItem<T>(store: string, key: string) : Promise<T | null>
  {
    const database = await this.getDatabase();

    return new Promise<T|null>((resolve, reject) =>
    {
      const transaction = database.transaction(store, "readonly");
      const objectStore = transaction.objectStore(store);
      const request     = objectStore.get(key);

      request.onsuccess = function()
      {
        if(request.result == null)
          resolve(null);
        else
          resolve(request.result);
      };

      request.onerror = function()
      {
        reject(request.error);
      };
    });
  }

  private static async getItemKeys(store: string): Promise<IDBValidKey[]>
  {
    const database = await this.getDatabase();

    return new Promise<IDBValidKey[]>((resolve, reject) =>
    {
      const transaction = database.transaction(store, "readonly");
      const objectStore = transaction.objectStore(store);
      const request     = objectStore.getAllKeys();

      request.onsuccess = function()
      {
        if(request.result)
          resolve(request.result);
        else
          resolve(null);
      };

      request.onerror = function()
      {
        reject(request.error);
      };
    });
  }

  public static async deleteItem(store: string, key: string) : Promise<void>
  {
    const database = await this.getDatabase();

    return new Promise<void>((resolve, reject) =>
    {
      const transaction = database.transaction(store, "readwrite");
      const objectStore       = transaction.objectStore(store);
      const request     = objectStore.delete(key);

      request.onsuccess = function()
      {
        resolve();
      };

      request.onerror = function()
      {
        reject(request.error);
      };
    });
  }

  private static async clear(store: string) : Promise<void>
  {
    const database = await this.getDatabase();

    return new Promise<void>((resolve, reject) =>
    {
      const transaction = database.transaction(store, "readwrite");
      const objectStore       = transaction.objectStore(store);
      const request     = objectStore.clear();

      request.onsuccess = function()
      {
        resolve();
      };

      request.onerror = function()
      {
        reject(request.error);
      };
    });
  }
}


// WEBPACK FOOTER //
// ./src/app/shared/helpers/indexed-db.helper.ts